import * as _ from 'lodash';
import * as React from 'react';
import Select from 'react-select';

import {
  Button, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, withStyles,
  WithStyles
} from '@material-ui/core';

import {
  AddRoleRequest, AuthUserDetails, DeleteRoleRequest, Role, ServiceDetails, UserDetails
} from './models/entities';

const styles = createStyles({
    selectControl: {
        margin: "10px",
        display: "block"
    },
    dialogContent: {
        minWidth: "400px",
        overflowY: "inherit"
    },
    paper: {
        overflowY: "visible"
    },
    selectLabel: {
        display: "inline-block",
        color: "#7f7f7f",
        textAlign: "right",
        marginRight: "5%",
        width: "20%"
    },
    select: {
        display: "inline-block",
        width: "75%"
    }
})

interface Props extends WithStyles<typeof styles> {
    userRole: UserDetails | undefined,
    isOpen: boolean,
    closeDialog: () => void,
    userRoleMap: { [key: string]: UserDetails },
    allUserMap: { [key: string]: AuthUserDetails },
    serviceRoleMap: { [key: string]: ServiceDetails }
    updateRolesHandler: (addRequests: AddRoleRequest[], deleteRequests: DeleteRoleRequest[]) => Promise<boolean>
}

interface State {
    isEditMode: boolean,
    selectedUser: AuthUserDetails | undefined,
    selectedService: ServiceDetails | undefined,
    selectedRoles: Role[],
    addRequest: AddRoleRequest | undefined,
    deleteRequest: DeleteRoleRequest | undefined
}

function getOptionsFromRoles(roles: Role[]) {
    return roles.map(role => ({ label: role.name, value: role.name }))
}

class EditRoleDialog extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            isEditMode: false,
            selectedUser: undefined,
            selectedService: undefined,
            selectedRoles: [],
            addRequest: undefined,
            deleteRequest: undefined
        }
    }

    public componentDidUpdate = (prevProps: Props) => {
        if ((prevProps.userRole === undefined && this.props.userRole !== undefined) ||
            (prevProps.userRole !== undefined && this.props.userRole !== undefined &&
                (prevProps.userRole.userId !== this.props.userRole.userId ||
                    prevProps.userRole.serviceId !== this.props.userRole.serviceId))) {
            this.setState({
                isEditMode: true,
                selectedUser: this.props.userRole,
                selectedService: this.props.serviceRoleMap[this.props.userRole.serviceId],
                selectedRoles: this.props.userRole.roles
            });
        }
        if (prevProps.userRole !== undefined && this.props.userRole === undefined) {
            this.setState({
                isEditMode: false,
                selectedUser: undefined,
                selectedService: undefined,
                selectedRoles: []
            });
        }
    }

    public render() {
        const { classes } = this.props;
        return (
            <Dialog open={this.props.isOpen} onBackdropClick={this.props.closeDialog} classes={{ paper: classes.paper }}>
                <DialogTitle>Add/Edit Roles</DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <FormControl className={classes.selectControl}>
                        <label className={classes.selectLabel}>Username:</label>
                        <Select
                        placeholder="Select Username"
                            className={classes.select}
                            options={Object.keys(this.props.allUserMap).map(key => this.props.allUserMap[key]).map(user => ({ label: user.username, value: user.userId }))}
                            isDisabled={this.state.isEditMode}
                            onChange={this.setSelectedUser}
                            value={this.state.selectedUser !== undefined ? { label: this.state.selectedUser.username, value: this.state.selectedUser.userId } : undefined}
                        />
                    </FormControl>
                    <FormControl className={classes.selectControl}>
                        <label className={classes.selectLabel}>Service:</label>
                        <Select
                        placeholder="Select Service"
                            className={classes.select}
                            options={Object.keys(this.props.serviceRoleMap).map(key => this.props.serviceRoleMap[key].serviceId).map(serviceId => ({ label: serviceId, value: serviceId }))}
                            isDisabled={this.state.isEditMode}
                            onChange={this.setSelectedService}
                            value={this.state.selectedService !== undefined ? { label: this.state.selectedService.serviceId, value: this.state.selectedService.serviceId } : undefined}
                        />
                    </FormControl>
                    <FormControl className={classes.selectControl}>
                        <label className={classes.selectLabel}>Roles:</label>
                        <Select
                        placeholder="Assign Roles"
                        noOptionsMessage={()=>"Select a Service to display roles"}
                            className={classes.select}
                            isMulti={true}
                            options={this.state && this.state.selectedService ? Object.keys(this.state.selectedService.roles).map(key => (this.state.selectedService as ServiceDetails).roles[key]).map(role => ({ label: role.name, value: role.name })) : []}
                            onChange={this.setSelectedRoles}
                            value={this.state.selectedRoles !== undefined ? getOptionsFromRoles(this.state.selectedRoles) : []}
                        />
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.executeUpdate} disabled={this.state.addRequest === undefined && this.state.deleteRequest === undefined}>Save</Button>
                </DialogActions>
            </Dialog>
        );
    }

    private setSelectedUser = (val: { label: string, value: string }) => {
        if (val !== undefined && val !== null) {
            this.setState({
                ...this.state,
                selectedUser: this.props.allUserMap[val.value]
            });
        }
    }

    private setSelectedService = (val: { label: string, value: string }) => {
        if (val !== undefined && val !== null) {
            let selectedRoles: Role[] = [];
            if (this.state.selectedUser !== undefined && this.props.userRoleMap[`${this.state.selectedUser.userId}#${val.value}`] !== undefined) {
                selectedRoles = this.props.userRoleMap[`${this.state.selectedUser.userId}#${val.value}`].roles
            }
            this.setState({
                ...this.state,
                selectedService: this.props.serviceRoleMap[val.value],
                selectedRoles
            });
        }
    }

    private setSelectedRoles = async (values: Array<{ label: string, value: string }>) => {
        if (values !== undefined && values !== null) {
            await this.setState({
                ...this.state,
                selectedRoles: values.map(val => (this.state.selectedService as ServiceDetails).roles[val.value]).map((roleDetails): Role => ({ name: roleDetails.name, status: 'ENABLED', ttl: roleDetails.defaultTTL }))
            });
            this.updateRequests();
        }
    }

    private updateRequests = () => {
        let addRoles: Role[] = [];
        let deleteRoles: Role[] = [];
        if (this.state.selectedUser !== undefined && this.state.selectedService !== undefined) {
            if (this.props.userRole !== undefined) {
                addRoles = _.differenceWith<Role, Role>(this.state.selectedRoles, this.props.userRole.roles, _.isEqual);
                deleteRoles = _.differenceWith<Role, Role>(this.props.userRole.roles, this.state.selectedRoles, _.isEqual);
            } else {
                addRoles = this.state.selectedRoles;
            }
        }
        const userId = this.state.selectedUser !== undefined ? this.state.selectedUser.userId : '';
        const serviceId = this.state.selectedService !== undefined ? this.state.selectedService.serviceId : '';
        const addRequest: AddRoleRequest | undefined = addRoles.length > 0 ? {
            userId,
            serviceId,
            roles: addRoles
        } : undefined;
        const deleteRequest: DeleteRoleRequest | undefined = deleteRoles.length > 0 ? {
            userId,
            serviceId,
            roleNames: deleteRoles.map(role => role.name)
        } : undefined;
        this.setState({
            ...this.state,
            addRequest,
            deleteRequest
        })
    }

    private executeUpdate = async () => {
        const addRequests: AddRoleRequest[] = [];
        const deleteRequests: DeleteRoleRequest[] = [];
        if (this.state.addRequest !== undefined) {
            addRequests.push(this.state.addRequest);
        }
        if (this.state.deleteRequest !== undefined) {
            deleteRequests.push(this.state.deleteRequest);
        }
        this.props.updateRolesHandler(addRequests, deleteRequests).finally(this.props.closeDialog);
    }
}

export default withStyles(styles)(EditRoleDialog);


// WEBPACK FOOTER //
// ./src/EditRoleDialog.tsx